import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import SEO from '../components/SEO'
import {
  fetchLatestRelease,
  detectOS,
  formatBytes,
  type ReleaseInfo,
  type ReleaseAsset,
} from '../utils/github-releases'
import './DownloadPage.css'

type Platform = 'windows' | 'macos' | 'linux' | 'android' | 'ios'

const PLATFORM_META: Record<Platform, { label: string; icon: string; description: string; comingSoon?: boolean }> = {
  windows: {
    label: 'Windows',
    icon: '🪟',
    description: 'Windows 10 / 11 (x64)',
  },
  macos: {
    label: 'macOS',
    icon: '🍎',
    description: 'macOS 13.0+ (Apple Silicon)',
  },
  linux: {
    label: 'Linux',
    icon: '🐧',
    description: 'Ubuntu 22.04+ / Debian (x64)',
  },
  android: {
    label: 'Android',
    icon: '🤖',
    description: 'Android 8.0+ (ARM64)',
  },
  ios: {
    label: 'iOS',
    icon: '📱',
    description: 'iPhone & iPad',
    comingSoon: true,
  },
}

function DownloadPage() {
  const [release, setRelease] = useState<ReleaseInfo | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(false)
  const [activePlatform, setActivePlatform] = useState<Platform>(detectOS)

  useEffect(() => {
    fetchLatestRelease()
      .then((r) => {
        setRelease(r)
        setLoading(false)
      })
      .catch(() => {
        setError(true)
        setLoading(false)
      })
  }, [])

  const platformAssets = (p: Platform): ReleaseAsset[] =>
    release?.assets.filter((a) => a.platform === p) ?? []

  const primaryAsset = (p: Platform): ReleaseAsset | undefined => {
    const assets = platformAssets(p)
    // Prefer: exe for windows, dmg (aarch64 first) for macos, AppImage for linux, apk for android
    if (p === 'windows') return assets.find((a) => a.format === 'exe')
    if (p === 'macos') return assets.find((a) => a.arch === 'aarch64') ?? assets[0]
    if (p === 'linux') return assets.find((a) => a.format === 'AppImage') ?? assets[0]
    if (p === 'android') return assets.find((a) => a.format === 'apk') ?? assets[0]
    return assets[0]
  }

  return (
    <div className="download-page">
      <SEO
        title="Download Intervio"
        description="Download Intervio for Windows, macOS, Linux, or Android. Real-time AI interview hints, invisible to screen sharing."
        path="/download"
      />

      <section className="download-hero">
        <div className="container">
          <h1>Download Intervio</h1>
          <p className="download-subtitle">
            Get real-time AI interview hints on desktop & mobile.
            {release && (
              <span className="download-version">
                {' '}Latest version: <strong>{release.version}</strong>
              </span>
            )}
          </p>
        </div>
      </section>

      <section className="download-content">
        <div className="container">
          {/* Platform Tabs */}
          <div className="platform-tabs" role="tablist">
            {(Object.keys(PLATFORM_META) as Platform[]).map((p) => (
              <button
                key={p}
                role="tab"
                aria-selected={activePlatform === p}
                className={`platform-tab ${activePlatform === p ? 'active' : ''} ${PLATFORM_META[p].comingSoon ? 'coming-soon' : ''}`}
                onClick={() => setActivePlatform(p)}
              >
                <span className="platform-tab-icon">{PLATFORM_META[p].icon}</span>
                <span>{PLATFORM_META[p].label}</span>
                {PLATFORM_META[p].comingSoon && <span className="coming-soon-badge">Soon</span>}
              </button>
            ))}
          </div>

          {/* Download Card */}
          <div className="download-card-main" role="tabpanel">
            {loading && (
              <div className="download-state">
                <div className="download-spinner" />
                <p>Checking latest release...</p>
              </div>
            )}

            {!loading && (error || !release) && (
              <div className="download-state">
                <p className="download-no-release">
                  No releases available yet. Please check back soon!
                </p>
              </div>
            )}

            {!loading && release && (
              <>
                <div className="download-platform-header">
                  <span className="download-platform-icon">
                    {PLATFORM_META[activePlatform].icon}
                  </span>
                  <div>
                    <h2>{PLATFORM_META[activePlatform].label}</h2>
                    <p className="download-platform-desc">
                      {PLATFORM_META[activePlatform].description}
                    </p>
                  </div>
                </div>

                {/* Coming Soon state (iOS) */}
                {PLATFORM_META[activePlatform].comingSoon ? (
                  <div className="download-coming-soon">
                    <div className="coming-soon-icon">🚧</div>
                    <h3>Coming Soon</h3>
                    <p>
                      We're working on the {PLATFORM_META[activePlatform].label} version.
                      Stay tuned — it will be available in a future release.
                    </p>
                  </div>
                ) : (
                  <>
                {/* Primary download */}
                {primaryAsset(activePlatform) ? (
                  <a
                    href={primaryAsset(activePlatform)!.downloadUrl}
                    className="btn btn-primary btn-large download-primary-btn"
                  >
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4" />
                      <polyline points="7 10 12 15 17 10" />
                      <line x1="12" y1="15" x2="12" y2="3" />
                    </svg>
                    Download for {PLATFORM_META[activePlatform].label}
                    {primaryAsset(activePlatform)!.size ? (
                      <span className="download-size">
                        ({formatBytes(primaryAsset(activePlatform)!.size!)})
                      </span>
                    ) : null}
                  </a>
                ) : (
                  <p className="download-not-available">
                    No installer available for {PLATFORM_META[activePlatform].label} in this release.
                  </p>
                )}

                {/* All assets for this platform */}
                {platformAssets(activePlatform).length > 1 && (
                  <div className="download-alt-assets">
                    <h4>All downloads for {PLATFORM_META[activePlatform].label}</h4>
                    <ul>
                      {platformAssets(activePlatform).map((a) => (
                        <li key={a.name}>
                          <a href={a.downloadUrl} className="download-alt-link">
                            <span className="download-alt-name">{a.name}</span>
                            <span className="download-alt-meta">
                              {a.arch} &middot; .{a.format}{a.size ? ` · ${formatBytes(a.size)}` : ''}
                            </span>
                          </a>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </>
                )}
              </>
            )}
          </div>

          {/* Installation instructions for unsigned apps */}
          {!PLATFORM_META[activePlatform].comingSoon && (
          <div className="download-install-instructions">
            <h3>Installation Instructions</h3>

            <div className="install-disclaimer">
              <p>
                <strong>Why is my OS yelling at me?</strong> Intervio captures
                your screen and audio in stealth mode — which is <em>exactly</em> what spyware
                does. Your operating system is doing its job. So you'll need one extra step to tell your OS you trust us.
              </p>
              <p>
                <strong>PS</strong>. Please don't show the app your bank credentials, passwords, or
                other secrets. We don't store your data, but text from your screen and
                microphone is sent to an LLM for processing — and that's not a place for
                your credit card number.
              </p>
              <p>
                <strong>PSS</strong>. If you still plan to share sensitive information with an app that secretly
                reads your screen and listens to your mic — we respectfully ask you
                not to use Intervio for interviewing for an IT Security Engineer position.
                You may need a bit more practice with that subject first. 😅
              </p>
            </div>

            {activePlatform === 'macos' && (
              <div className="install-steps">
                <h4>🍎 macOS</h4>
                <ol>
                  <li>Download and open the <code>.dmg</code> file, then drag Intervio to your <strong>Applications</strong> folder.</li>
                  <li>Double-click <strong>Intervio</strong> to launch it. macOS will show a dialog saying the app can't be opened.</li>
                  <li>Open <strong>System Settings → Privacy & Security</strong>.</li>
                  <li>Scroll down — you will see a message about Intervio being blocked. Click <strong>"Open Anyway"</strong>.</li>
                  <li>Confirm by clicking <strong>"Open"</strong> in the next dialog. macOS will remember your choice — subsequent launches will work normally.</li>
                </ol>
                <div className="install-alt">
                  <strong>Alternative (Terminal):</strong>
                  <code className="install-code-block">xattr -cr /Applications/Intervio.app</code>
                  <span className="install-alt-note">Run this once, then open the app normally.</span>
                </div>
              </div>
            )}

            {activePlatform === 'windows' && (
              <div className="install-steps">
                <h4>🪟 Windows</h4>
                <ol>
                  <li>Download and run the <code>.exe</code> installer.</li>
                  <li>Windows SmartScreen may show <strong>"Windows protected your PC"</strong>.</li>
                  <li>Click <strong>"More info"</strong> to reveal additional options.</li>
                  <li>Click <strong>"Run anyway"</strong> to proceed with the installation.</li>
                  <li>Follow the installer prompts — the app will launch normally after installation.</li>
                </ol>
                <div className="install-alt">
                  <strong>Alternative (PowerShell):</strong>
                  <code className="install-code-block">Unblock-File -Path "$env:USERPROFILE\Downloads\Intervio_*_x64-setup.exe"</code>
                  <span className="install-alt-note">Run this before launching the installer to skip the SmartScreen prompt.</span>
                </div>
              </div>
            )}

            {activePlatform === 'linux' && (
              <div className="install-steps">
                <h4>🐧 Linux — No extra steps needed</h4>
                <ol>
                  <li>For <code>.AppImage</code>: make the file executable (<code>chmod +x Intervio_*.AppImage</code>) and run it.</li>
                  <li>For <code>.deb</code>: install with <code>sudo dpkg -i intervio_*.deb</code> or double-click to open in your package manager.</li>
                </ol>
              </div>
            )}

            {activePlatform === 'android' && (
              <div className="install-steps">
                <h4>🤖 Android</h4>
                <ol>
                  <li>Download the <code>.apk</code> file on your Android device (or transfer it from your computer).</li>
                  <li>Open the file. Android may show <strong>"Install blocked"</strong> or <strong>"Unknown sources"</strong> warning.</li>
                  <li>Tap <strong>"Settings"</strong> in the dialog, then enable <strong>"Allow from this source"</strong> (for your browser or file manager).</li>
                  <li>Go back and tap <strong>"Install"</strong> to proceed.</li>
                  <li>After installation, open Intervio and log in with your account.</li>
                </ol>
                <div className="install-alt">
                  <strong>Tip:</strong>
                  <span className="install-alt-note">On some devices, you can find this setting under Settings → Apps → Special access → Install unknown apps.</span>
                </div>
              </div>
            )}
          </div>
          )}

          {/* System requirements */}
          {!PLATFORM_META[activePlatform].comingSoon && (
          <div className="download-requirements">
            <h3>System Requirements</h3>
            <div className="requirements-grid">
              <div className="requirement-card">
                <h4>🪟 Windows</h4>
                <ul>
                  <li>Windows 10 (1803) or later</li>
                  <li>x64 processor</li>
                  <li>4 GB RAM minimum</li>
                  <li>WebView2 (auto-installed)</li>
                </ul>
              </div>
              <div className="requirement-card">
                <h4>🍎 macOS</h4>
                <ul>
                  <li>macOS 13.0 (Ventura) or later</li>
                  <li>Apple Silicon (M1 / M2 / M3)</li>
                  <li>4 GB RAM minimum</li>
                </ul>
              </div>
              <div className="requirement-card">
                <h4>🐧 Linux</h4>
                <ul>
                  <li>Ubuntu 22.04+ / Debian-based</li>
                  <li>x64 processor</li>
                  <li>4 GB RAM minimum</li>
                  <li>WebKitGTK 4.1</li>
                </ul>
              </div>
              <div className="requirement-card">
                <h4>🤖 Android</h4>
                <ul>
                  <li>Android 8.0 (Oreo) or later</li>
                  <li>ARM64 processor</li>
                  <li>3 GB RAM minimum</li>
                  <li>Camera & microphone</li>
                </ul>
              </div>
            </div>
          </div>
          )}

          {/* Footer links */}
          <div className="download-footer-links">
            <Link to="/how-it-works" className="download-how-link">
              How does Intervio work? &rarr;
            </Link>
          </div>
        </div>
      </section>
    </div>
  )
}

export default DownloadPage
